import { useQuery } from '@tanstack/react-query'
import { type ConfigArea, type PermissionSetEntity } from '@omnea-app/authz'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { PermissionsHttpClient } from '@omnea-app/permissions/permissionsHttpClient'
import { type paths } from '@omnea-app/permissions/permissionsHttpClient/permissionsGeneratedTypes'

export const GetAuthzContextQueryKey = 'GetAuthzContext'

const fiveMinutesMs = 5 * 60 * 1000

/**
 * `unavailable` is a check that did not answer, which is not the same as `resolved` with empty sets:
 * empty sets are a denial, and callers may treat the two differently. It carries the error so a
 * caller can report the failure rather than present a check that will never complete as still running.
 *
 * TODO(Permissions): remove the `legacy` state and every caller's fallback branch once the
 * permissions migration completes and no organisation answers 404.
 */
export type AuthzContextState =
  | { status: 'loading' | 'legacy' }
  | { status: 'unavailable'; error: unknown }
  | {
      status: 'resolved'
      entities: ReadonlySet<PermissionSetEntity>
      managedAreas: ReadonlySet<ConfigArea>
      canManagePermissions: boolean
    }

const LOADING: AuthzContextState = { status: 'loading' }

export const LEGACY_AUTHZ_CONTEXT: AuthzContextState = { status: 'legacy' }

type AuthzContextBody = paths['/authz-context']['get']['responses'][200]['content']['application/json']

/** The 404 is cached as data rather than thrown, so the not-on-FGA answer survives a refetch. */
type AuthzContextResponse = { kind: 'authorized'; body: AuthzContextBody } | { kind: 'legacy' }

/** react-query reuses a select result only while the function keeps its reference. */
const toAuthzContextState = (response: AuthzContextResponse): AuthzContextState => {
  if (response.kind === 'legacy') return LEGACY_AUTHZ_CONTEXT
  const { entities, configAreas, canManagePermissions } = response.body
  return {
    status: 'resolved',
    entities: new Set(entities.map(({ kind }) => kind)),
    managedAreas: new Set(configAreas.filter(({ canManage }) => canManage).map(({ area }) => area)),
    canManagePermissions,
  }
}

/**
 * What the current user has access to, resolved in one call at boot: the entities they have
 * access to, the config areas they manage, and whether they may administer permissions.
 *
 * An entity is present on entitlement, not on there being records to show — a grant conferring nothing
 * today still means access, since the caller may create the first record.
 *
 * Access is conveyed by presence, so an absent entity is a denial and empty sets are a real answer.
 */
export const useAuthzContextQuery = (): AuthzContextState => {
  const { getToken, user } = useAuthenticatedUser()
  const { data, isError, error } = useQuery({
    queryKey: [GetAuthzContextQueryKey, user.id],
    queryFn: async (): Promise<AuthzContextResponse> => {
      const authorization = await getToken()
      const permissionsClient = new PermissionsHttpClient({ authorization })
      const { data: response, error } = await permissionsClient.GET('/authz-context', {})
      if (error != null && 'type' in error && error.type === 'NotFoundError') return { kind: 'legacy' }
      throwIfApiError(error)
      if (response == null) throw new Error('authz context request returned no body')
      return { kind: 'authorized', body: response }
    },
    select: toAuthzContextState,
    // Access rarely changes, so the shared client's seconds-long default would refetch constantly.
    staleTime: fiveMinutesMs,
  })

  // A failed background refetch keeps the last-known answer rather than pulling sections mid-session.
  if (data != null) return data
  if (isError) return { status: 'unavailable', error }
  return LOADING
}
